import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Image as ImageIcon, Loader2, MessageCircle, RefreshCw, Share2, ThumbsUp, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { errMsg, fmtDate } from "@/lib/api";

function Stat({ icon: Icon, label, value, id }) {
  return (
    <div className="panel p-5" data-testid={id}>
      <div className="flex items-center justify-between"><p className="eyebrow">{label}</p><Icon className="h-4 w-4 text-primary" /></div>
      <p className="mt-3 font-mono text-3xl">{value}</p>
    </div>
  );
}

const Num = ({ v }) => (v == null ? <span className="text-muted-foreground">-</span> : <span className="font-mono">{v}</span>);

export default function PagePerformance() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("recent");
  const [busy, setBusy] = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try { const r = await api.get("/page/insights", { params: { sort, refresh }, timeout: 120000 }); setData(r.data); }
    catch (e) { setData((d) => d || { connected: false, items: [], totals: { reactions: 0, comments: 0, shares: 0 }, avg_engagement: 0, error: errMsg(e) }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  const best = data.items.reduce((b, x) => (x.engagement > (b?.engagement ?? 0) ? x : b), null);

  return (
    <div data-testid="page-performance">
      {!data.connected && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="page-perf-not-connected">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Facebook Page belum disambung, jadi angka terkini tidak dapat diambil. Sambung Page (token perlu kebenaran <code>pages_read_engagement</code>) di <Link to="/tetapan" className="underline">Tetapan</Link>.</span>
        </div>
      )}
      {data.error && <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-200" data-testid="page-perf-error"><TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /><span>Gagal ambil data dari Facebook: {data.error}</span></div>}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={ThumbsUp} label="Reaksi" value={data.totals.reactions} id="page-perf-total-reactions" />
        <Stat icon={MessageCircle} label="Komen" value={data.totals.comments} id="page-perf-total-comments" />
        <Stat icon={Share2} label="Share" value={data.totals.shares} id="page-perf-total-shares" />
        <Stat icon={RefreshCw} label="Purata / post" value={data.avg_engagement} id="page-perf-avg" />
      </div>

      {best && best.engagement > 0 && (
        <div className="panel mb-6 p-5" data-testid="page-perf-best">
          <p className="eyebrow">Ayat paling berkesan</p>
          <p className="mt-2 line-clamp-3 whitespace-pre-line text-sm">{best.text}</p>
          <p className="mt-2 text-xs text-muted-foreground">{best.stats.reactions} reaksi · {best.stats.comments} komen · {best.stats.shares} share</p>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{data.count} post Page{data.page_name && <> · <b className="text-slate-200">{data.page_name}</b></>}</p>
        <div className="flex gap-2">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-48 bg-card" data-testid="page-perf-sort"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="recent">Terbaharu dahulu</SelectItem><SelectItem value="engagement">Engagement tertinggi</SelectItem></SelectContent>
          </Select>
          <Button variant="secondary" size="sm" className="h-9" onClick={() => load(true)} disabled={busy || !data.connected} data-testid="page-perf-refresh">{busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}Muat semula</Button>
        </div>
      </div>

      {!data.items.length ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="page-perf-empty">Belum ada post yang berjaya dihantar ke Facebook Page.</div>
      ) : (
        <div className="panel overflow-x-auto">
          <Table data-testid="page-perf-table">
            <TableHeader><TableRow className="border-border"><TableHead>Post</TableHead><TableHead>Tarikh</TableHead><TableHead className="text-right">Reaksi</TableHead><TableHead className="text-right">Komen</TableHead><TableHead className="text-right">Share</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {data.items.map((x) => (
                <TableRow key={x.tid} className="border-border" data-testid={`page-perf-row-${x.tid}`}>
                  <TableCell className="max-w-md"><p className="line-clamp-2 text-sm">{x.has_image && <ImageIcon className="mr-1 inline h-3.5 w-3.5 text-muted-foreground" />}{x.text}</p></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(x.done_at)}</TableCell>
                  <TableCell className="text-right"><Num v={x.stats?.reactions} /></TableCell>
                  <TableCell className="text-right"><Num v={x.stats?.comments} /></TableCell>
                  <TableCell className="text-right"><Num v={x.stats?.shares} /></TableCell>
                  <TableCell className="text-right font-semibold"><Num v={x.stats ? x.engagement : null} /></TableCell>
                  <TableCell>{x.stats?.permalink && <a href={x.stats.permalink} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80" data-testid="page-perf-open"><ExternalLink className="h-4 w-4" /></a>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">Angka dikemas kini dari Facebook setiap 1 jam (atau tekan Muat semula).</p>
    </div>
  );
}
